<template>
 <div class="dashboard">
 <el-row :gutter="20" class="card-row">
 <el-col :span="6" v-for="card in overviewCards" :key="card.label">
 <div class="stat-card" :class="card.theme">
 <div class="stat-card-top">
 <div class="stat-card-icon"><i :class="card.icon"></i></div>
 <div class="stat-card-info">
 <div class="stat-card-value">{{ card.value }}</div>
 <div class="stat-card-label">{{ card.label }}</div>
 </div>
 </div>
 <div class="stat-card-sub">{{ card.sub || '\u00a0' }}</div>
 </div>
 </el-col>
 </el-row>

 <el-row :gutter="20" class="card-row">
 <el-col :span="16">
 <div class="chart-card">
 <div class="chart-header">
 <el-date-picker
 v-model="dateRange"
 type="daterange"
 range-separator="至"
 start-placeholder="开始日期"
 end-placeholder="结束日期"
 value-format="yyyy-MM-dd"
 :picker-options="pickerOptions"
 @change="loadAll"
 />
 <el-select v-model="trendGranularity" size="small" style="width:90px; margin-left:12px" @change="onTrendGranularityChange">
 <el-option label="分" value="minute" />
 <el-option label="时" value="hour" />
 <el-option label="日" value="day" />
 <el-option label="周" value="week" />
 <el-option label="月" value="month" />
 <el-option label="年" value="year" />
 </el-select>
 <el-button type="primary" size="small" @click="loadAll" style="margin-left:12px">刷新</el-button>
 </div>
 <div ref="trendChart" class="chart-body"></div>
 </div>
 </el-col>
 <el-col :span="8">
 <div class="chart-card">
 <div class="chart-header"><span class="chart-title">访问来源分布</span></div>
 <div ref="refererChart" class="chart-body"></div>
 </div>
 </el-col>
 </el-row>

 <el-row :gutter="20" class="card-row">
 <el-col :span="8">
 <div class="chart-card">
 <div class="chart-header"><span class="chart-title">浏览器分布</span></div>
 <div ref="browserChart" class="chart-body"></div>
 </div>
 </el-col>
 <el-col :span="8">
 <div class="chart-card">
 <div class="chart-header"><span class="chart-title">操作系统</span></div>
 <div ref="osChart" class="chart-body"></div>
 </div>
 </el-col>
 <el-col :span="8">
 <div class="chart-card">
 <div class="chart-header"><span class="chart-title">设备类型</span></div>
 <div ref="deviceChart" class="chart-body"></div>
 </div>
 </el-col>
 </el-row>

 <el-row :gutter="20" class="card-row">
 <el-col :span="24">
 <div class="chart-card">
 <div class="chart-header"><span class="chart-title">网站访问排名</span></div>
 <div ref="rankingChart" class="chart-body"></div>
 </div>
 </el-col>
 </el-row>
 </div>
</template>

<script>
import * as echarts from 'echarts';
import { statsApi } from '../api';
import dayjs from 'dayjs';

export default {
 name: 'Dashboard',
 data() {
 return {
 dateRange: [dayjs().subtract(30, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
 overview: {},
 trendGranularity: 'day',
 charts: {},
 pickerOptions: {
 shortcuts: [
 { text: '今天', onClick(picker) { const d = dayjs().format('YYYY-MM-DD'); picker.$emit('pick', [d, d]); } },
 { text: '近7天', onClick(picker) { picker.$emit('pick', [dayjs().subtract(6, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]); } },
 { text: '近30天', onClick(picker) { picker.$emit('pick', [dayjs().subtract(29, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]); } },
 { text: '本月', onClick(picker) { picker.$emit('pick', [dayjs().startOf('month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]); } },
 ]
 }
 };
 },
 computed: {
 overviewCards() {
 const o = this.overview;
 return [
 { label: '总访问量', value: o.total_visits || 0, icon: 'el-icon-data-line', theme: 'blue', sub: `今日 ${o.today_visits || 0} / 昨日 ${o.yesterday_visits || 0}` },
 { label: '独立访客', value: o.unique_visitors || 0, icon: 'el-icon-user', theme: 'green', sub: `新访客 ${o.new_visitors || 0}` },
 { label: '独立IP', value: o.unique_ips || 0, icon: 'el-icon-connection', theme: 'orange' },
 { label: '新访客数', value: o.new_visitors || 0, icon: 'el-icon-star-on', theme: 'red' },
 ];
 }
 },
 mounted() {
 this.loadAll();
 window.addEventListener('resize', this.resizeCharts);
 },
 beforeDestroy() {
 window.removeEventListener('resize', this.resizeCharts);
 Object.values(this.charts).forEach(c => c && c.dispose());
 },
 methods: {
 async loadAll() {
 const [startDate, endDate] = this.dateRange || [];
 const params = { start_date: startDate, end_date: endDate };
 this.loadOverview(params);
 this.loadTrend(params);
 this.loadReferer(params);
 this.loadDevice(params);
 this.loadRanking(params);
 },
 async loadOverview(params) {
 try {
 const res = await statsApi.overview(params);
 if (res.code === 0) this.overview = res.data;
 } catch (e) { console.error(e); }
 },
 async loadTrend(params) {
 try {
 const res = await statsApi.trend({ ...params, granularity: this.trendGranularity });
 if (res.code !== 0) return;
 const { labels, visits, visitors, ips, new_visitors } = res.data;
 this.renderChart('trendChart', {
 tooltip: { trigger: 'axis', backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
 legend: { data: ['访问量', '独立访客', '独立IP', '新访客'], top: 5, textStyle: { fontSize: 12 } },
 grid: { left: 50, right: 25, top: 55, bottom: 60 },
 xAxis: { type: 'category', data: labels, axisLabel: { rotate: 30, fontSize: 11, color: '#64748b' }, axisLine: { lineStyle: { color: '#cbd5e1' } } },
 yAxis: { type: 'value', axisLabel: { color: '#64748b' }, splitLine: { lineStyle: { color: '#f1f3f5' } } },
 dataZoom: [{ type: 'inside' }, { type: 'slider', height: 20, bottom: 10, borderColor: '#e2e8f0', fillerColor: 'rgba(91,141,239,0.06)' }],
 series: [
 { name: '访问量', type: 'line', smooth: true, data: visits, itemStyle: { color: '#5b8def' }, lineStyle: { width: 2 }, areaStyle: { color: new echarts.graphic.LinearGradient(0,0,0,1,[{offset:0,color:'rgba(91,141,239,0.12)'},{offset:1,color:'rgba(91,141,239,0)'}]) } },
 { name: '独立访客', type: 'line', smooth: true, data: visitors, itemStyle: { color: '#6b9e72' }, lineStyle: { width: 2 } },
 { name: '独立IP', type: 'line', smooth: true, data: ips, itemStyle: { color: '#d4905a' }, lineStyle: { width: 2 } },
 { name: '新访客', type: 'line', smooth: true, data: new_visitors, itemStyle: { color: '#c76b6b' }, lineStyle: { width: 2 } },
 ]
 });
 } catch (e) { console.error(e); }
 },
 onTrendGranularityChange() {
 const [startDate, endDate] = this.dateRange || [];
 this.loadTrend({ start_date: startDate, end_date: endDate });
 },
 async loadReferer(params) {
 try {
 const res = await statsApi.referer({ ...params, limit: 10 });
 if (res.code !== 0) return;
 const data = res.data.map(item => ({ name: item.name || '直接访问', value: item.count }));
 this.renderChart('refererChart', {
 tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)', backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
 legend: { type: 'scroll', bottom: 5, textStyle: { fontSize: 11 } },
 series: [{
 type: 'pie', radius: ['38%', '62%'], center: ['50%', '45%'],
 data, label: { fontSize: 11, color: '#475569' },
 itemStyle: { borderRadius: 6, borderColor: '#fff', borderWidth: 2 },
 color: ['#5b8def','#6b9e72','#d4905a','#c76b6b','#8b7ec8','#5fa8c4','#c4a857','#6dba8a','#64748b','#cbd5e1']
 }]
 });
 } catch (e) { console.error(e); }
 },
 async loadDevice(params) {
 try {
 const res = await statsApi.device(params);
 if (res.code !== 0) return;
 const { browsers, oses, devices } = res.data;
 const pieColors = ['#5b8def','#6b9e72','#d4905a','#c76b6b','#8b7ec8','#5fa8c4','#c4a857','#6dba8a'];
 this.renderChart('browserChart', {
 tooltip: { trigger: 'item', backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
 series: [{
 type: 'pie', radius: ['35%', '62%'], center: ['50%', '50%'],
 data: browsers.map(b => ({ name: b.browser, value: +b.count })),
 label: { fontSize: 11, color: '#475569', formatter: '{b}\n{d}%' },
 itemStyle: { borderRadius: 5, borderColor: '#fff', borderWidth: 2 },
 color: pieColors
 }]
 });
 this.renderChart('osChart', {
 tooltip: { trigger: 'item', backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
 series: [{
 type: 'pie', radius: ['35%', '62%'], center: ['50%', '50%'],
 data: oses.map(o => ({ name: o.os, value: +o.count })),
 label: { fontSize: 11, color: '#475569', formatter: '{b}\n{d}%' },
 itemStyle: { borderRadius: 5, borderColor: '#fff', borderWidth: 2 },
 color: pieColors
 }]
 });
 this.renderChart('deviceChart', {
 tooltip: { trigger: 'item', backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
 series: [{
 type: 'pie', radius: ['35%', '62%'], center: ['50%', '50%'],
 data: devices.map(d => ({ name: d.device, value: +d.count })),
 label: { fontSize: 11, color: '#475569', formatter: '{b}\n{d}%' },
 itemStyle: { borderRadius: 5, borderColor: '#fff', borderWidth: 2 },
 color: pieColors
 }]
 });
 } catch (e) { console.error(e); }
 },
 async loadRanking(params) {
 try {
 const res = await statsApi.ranking({ ...params, limit: 10 });
 if (res.code !== 0) return;
 const data = res.data;
 const names = data.map(d => d.website_name || d.domain || '未知');
 const visits = data.map(d => d.total_visits);
 const visitors = data.map(d => d.unique_visitors);
 const ips = data.map(d => d.unique_ips);
 this.renderChart('rankingChart', {
 tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, backgroundColor: 'rgba(30,41,59,0.92)', borderColor: 'rgba(91,141,239,0.15)', textStyle: { color: '#fff', fontSize: 12 } },
 legend: { data: ['访问量', '独立访客', '独立IP'], top: 5, textStyle: { fontSize: 12 } },
 grid: { left: 60, right: 30, top: 55, bottom: 30 },
 xAxis: { type: 'category', data: names, axisLabel: { fontSize: 11, color: '#64748b', interval: 0, rotate: names.length > 6 ? 25 : 0 }, axisLine: { lineStyle: { color: '#cbd5e1' } } },
 yAxis: { type: 'value', axisLabel: { color: '#64748b' }, splitLine: { lineStyle: { color: '#f1f3f5' } } },
 series: [
 { name: '访问量', type: 'bar', data: visits, barMaxWidth: 40, itemStyle: { color: new echarts.graphic.LinearGradient(0,0,0,1,[{offset:0,color:'#5b8def'},{offset:1,color:'rgba(91,141,239,0.15)'}]), borderRadius: [4,4,0,0] } },
 { name: '独立访客', type: 'bar', data: visitors, barMaxWidth: 40, itemStyle: { color: new echarts.graphic.LinearGradient(0,0,0,1,[{offset:0,color:'#6b9e72'},{offset:1,color:'rgba(107,158,114,0.15)'}]), borderRadius: [4,4,0,0] } },
 { name: '独立IP', type: 'bar', data: ips, barMaxWidth: 40, itemStyle: { color: new echarts.graphic.LinearGradient(0,0,0,1,[{offset:0,color:'#d4905a'},{offset:1,color:'rgba(212,144,90,0.15)'}]), borderRadius: [4,4,0,0] } },
 ]
 });
 } catch (e) { console.error(e); }
 },
 renderChart(ref, option) {
 this.$nextTick(() => {
 const el = this.$refs[ref];
 if (!el) return;
 if (this.charts[ref]) this.charts[ref].dispose();
 this.charts[ref] = echarts.init(el);
 this.charts[ref].setOption(option);
 });
 },
 resizeCharts() {
 Object.values(this.charts).forEach(c => c && c.resize());
 },
 }
};
</script>

<style scoped>
.filter-bar { display: flex; align-items: center; margin-bottom: 18px; }
.card-row { margin-bottom: 18px; }
.card-row > .el-col { display: flex; }

.stat-card {
 background: #fff;
 border-radius: 8px;
 padding: 20px 22px;
 box-shadow: 0 1px 3px rgba(0,0,0,0.04);
 border: 1px solid #eef0f3;
 width: 100%;
 display: flex;
 flex-direction: column;
 border-left: 3px solid #e2e8f0;
 transition: box-shadow 0.2s;
}
.stat-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
.stat-card.blue { border-left-color: #5b8def; }
.stat-card.green { border-left-color: #6b9e72; }
.stat-card.orange { border-left-color: #d4905a; }
.stat-card.red { border-left-color: #c76b6b; }

.stat-card-top { display: flex; align-items: center; gap: 14px; }
.stat-card-icon {
 width: 40px; height: 40px;
 border-radius: 8px;
 display: flex; align-items: center; justify-content: center;
 font-size: 20px; flex-shrink: 0;
}
.stat-card.blue .stat-card-icon { background: rgba(91,141,239,0.08); color: #5b8def; }
.stat-card.green .stat-card-icon { background: rgba(107,158,114,0.08); color: #6b9e72; }
.stat-card.orange .stat-card-icon { background: rgba(212,144,90,0.08); color: #d4905a; }
.stat-card.red .stat-card-icon { background: rgba(199,107,107,0.08); color: #c76b6b; }

.stat-card-info { flex: 1; }
.stat-card-value { font-size: 26px; font-weight: 600; color: #1e293b; line-height: 1.3; }
.stat-card-label { font-size: 13px; color: #64748b; margin-top: 2px; }
.stat-card-sub { font-size: 12px; color: #94a3b8; margin-top: auto; padding-top: 10px; border-top: 1px solid #f5f6f8; min-height: 20px; }

.chart-card {
 background: #fff;
 border-radius: 8px;
 box-shadow: 0 1px 3px rgba(0,0,0,0.04);
 border: 1px solid #eef0f3;
 overflow: hidden;
 width: 100%;
}
.chart-header {
 padding: 14px 20px;
 border-bottom: 1px solid #f0f1f3;
 display: flex; align-items: center; justify-content: space-between;
}
.chart-title { font-size: 14px; font-weight: 600; color: #1e293b; }
.chart-body { height: 340px; padding: 12px; }
</style>